// Seitenleiste im Mietermodus (Handoff Mieter §5.4): Name, Einheit und Anzahl je Bereich.
// Nur Zählabfragen (head) – die Detailseite lädt die Inhalte selbst.

import { useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { heuteIso } from '../immobilien/format';
import { mieterName } from '../immobilien/objektDetail';
import { MIETER_GEAENDERT } from './useMieterDetail';

export interface MieterKopf {
  name: string;
  einheit: string;
  anzahl: { vertraege: number | null; dokumente: number | null; tickets: number | null; termine: number | null; notizen: number | null };
}

export type MieterKopfZustand = { status: 'laedt' } | { status: 'fehlt' } | { status: 'fehler' } | { status: 'ok'; kopf: MieterKopf };

const zahl = (r: { count: number | null; error: unknown }): number | null => (r.error ? null : r.count ?? 0);

export function useMieterKopf(mieterId: string | null): MieterKopfZustand {
  const [zustand, setZustand] = useState<MieterKopfZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    if (!mieterId) return;
    let aktiv = true;
    setZustand({ status: 'laedt' });
    (async () => {
      const m = await supabase.from('mieter').select('id, vorname, nachname, firma_name, einheit, einheit_id').eq('id', mieterId).maybeSingle();
      if (!aktiv) return;
      if (m.error) { setZustand({ status: 'fehler' }); return; }
      const roh = m.data as { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; einheit: string | null; einheit_id: string | null } | null;
      if (!roh) { setZustand({ status: 'fehlt' }); return; }
      const z = (tabelle: string) => supabase.from(tabelle).select('id', { count: 'exact', head: true });
      const [einheit, v, d, t, te, n] = await Promise.all([
        roh.einheit_id ? supabase.from('objekt_einheiten').select('bezeichnung').eq('id', roh.einheit_id).maybeSingle() : Promise.resolve(null),
        z('vertraege').eq('mieter_id', roh.id),
        z('dokumente').eq('mieter_id', roh.id).eq('is_deleted', false),
        z('tickets').eq('mieter_id', roh.id).is('geloescht_am', null).not('status', 'in', '(erledigt,geschlossen)'),
        z('termine').eq('mieter_id', roh.id).gte('start_datum', heuteIso()),
        z('kontakt_notizen').eq('quelle', 'mieter').eq('quelle_id', roh.id),
      ]);
      if (!aktiv) return;
      const bez = ((einheit?.data as { bezeichnung: string | null } | null)?.bezeichnung ?? '').trim();
      setZustand({
        status: 'ok',
        kopf: {
          name: mieterName(roh),
          einheit: bez || (roh.einheit ?? '').trim(),
          anzahl: { vertraege: zahl(v), dokumente: zahl(d), tickets: zahl(t), termine: zahl(te), notizen: zahl(n) },
        },
      });
    })().catch(() => { if (aktiv) setZustand({ status: 'fehler' }); });
    return () => { aktiv = false; };
  }, [mieterId, runde]);

  useEffect(() => {
    if (!mieterId) return;
    const hoeren = (e: Event) => { if ((e as CustomEvent<string>).detail === mieterId) setRunde(x => x + 1); };
    window.addEventListener(MIETER_GEAENDERT, hoeren);
    return () => window.removeEventListener(MIETER_GEAENDERT, hoeren);
  }, [mieterId]);

  return zustand;
}
